import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CategoryCard, ProductCard } from "../components/Cards";
import { Meta } from "../components/Meta";
import { routes } from "../config/routes";
import {
  categories,
  isEligibleForRegion,
  products,
  type Region
} from "../data/catalogue";

type RegionFilter = "all" | Region;

export function ShopPage() {
  const [searchParams] = useSearchParams();
  const requestedCategory = searchParams.get("category");
  const [category, setCategory] = useState(
    categories.some((item) => item.id === requestedCategory) ? requestedCategory! : "all"
  );
  const [region, setRegion] = useState<RegionFilter>("all");
  const [bundlesOnly, setBundlesOnly] = useState(false);

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const categoryMatches =
          category === "all" ||
          product.category.toLocaleLowerCase().replace(/\s+/g, "-") === category;
        const regionMatches =
          region === "all" || isEligibleForRegion(product, region);
        const bundleMatches = !bundlesOnly || product.bundleComponents.length > 0;
        return categoryMatches && regionMatches && bundleMatches;
      }),
    [bundlesOnly, category, region]
  );

  return (
    <main id="main">
      <Meta
        title="Shop | Beautify Ourselves"
        description="Explore the Cleansers, Moisturisers and Body Care collection coming soon to Beautify Ourselves."
        path={routes.shop}
      />
      <header className="page-hero page-hero-compact">
        <div className="page-hero-inner">
          <p className="eyebrow">The collection</p>
          <h1>Your beauty collection is taking shape.</h1>
          <p className="lead">
            Explore the categories we’re preparing, with clear product details and
            considered choices to follow.
          </p>
        </div>
      </header>
      <section className="section shop-catalogue">
        <div className="section-inner">
          {products.length === 0 ? (
            <div className="product-grid">
              {categories.map((item) => (
                <CategoryCard
                  key={item.id}
                  category={item}
                  linked={false}
                  variant="catalogue"
                />
              ))}
            </div>
          ) : (
            <>
              <div className="shop-tools">
                <div className="filters" aria-label="Filter catalogue by category">
                  <button
                    className="filter-button"
                    type="button"
                    aria-pressed={category === "all"}
                    onClick={() => setCategory("all")}
                  >
                    All
                  </button>
                  {categories.map((item) => (
                    <button
                      key={item.id}
                      className="filter-button"
                      type="button"
                      aria-pressed={category === item.id}
                      onClick={() => setCategory(item.id)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                <div className="filters" aria-label="Filter catalogue by delivery region">
                  {(["all", "uk", "ireland"] as RegionFilter[]).map((value) => (
                    <button
                      key={value}
                      className="filter-button"
                      type="button"
                      aria-pressed={region === value}
                      onClick={() => setRegion(value)}
                    >
                      {value === "all"
                        ? "All regions"
                        : value === "uk"
                          ? "United Kingdom"
                          : "Republic of Ireland"}
                    </button>
                  ))}
                  <button
                    className="filter-button"
                    type="button"
                    aria-pressed={bundlesOnly}
                    onClick={() => setBundlesOnly((value) => !value)}
                  >
                    Bundles
                  </button>
                </div>
              </div>
              <p aria-live="polite">{filteredProducts.length} products</p>
              <div className="product-grid">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
                {filteredProducts.length === 0 && (
                  <div className="empty-state">
                    <h2>No matching products</h2>
                    <p>Try changing the catalogue filters.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
